import { useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import Flex from 'shared/ui/Flex'
import { Skeleton } from 'shared/ui/Skeleton'
import SearchInput from 'shared/ui/SearchInput'
import useAuth from 'data/gradoo/hooks/useAuth'
import useContentMarks, {
  ContentMarkType
} from 'shared/hooks/useContentMarks'
import { REPORTS_INSTANCES } from 'data/gradoo/queries/reports'
import { ReportsInstanceFragment } from 'shared/types/gradoo/graphql'

import { PanelProps, Panels } from '../../types'
import ReportItem from './components/ReportItem'

type ReportsPanelProps = PanelProps & {
  moduleInstanceId?: string
}

const ReportsPanel: React.FC<ReportsPanelProps> = ({
  moduleInstanceId,
  push
}) => {
  const [search, setSearch] = useState('')
  const { authGroupId } = useAuth()

  const { markedIds, toggleMark } = useContentMarks({
    groupId: authGroupId ?? undefined,
    moduleInstanceId
  })

  const { data: reportsInstancesData, loading } = useQuery(
    REPORTS_INSTANCES,
    {
      skip: !moduleInstanceId,
      variables: {
        moduleInstance: moduleInstanceId
      },
      context: { client: 'gradoo' }
    }
  )

  const reportsInstances: ReportsInstanceFragment[] =
    (reportsInstancesData?.reportsInstances?.edges.map(
      edge => edge?.node
    ) as ReportsInstanceFragment[]) || []

  const filteredReportsInstances = useMemo(() => {
    return reportsInstances.filter(ri =>
      ri.title.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    )
  }, [reportsInstances, search])

  return (
    <Flex direction="column">
      <SearchInput
        value={search}
        onChange={setSearch}
        marginBottom={26}
      />
      <Flex direction="column" rowGap={16}>
        {loading
          ? new Array(3)
              .fill(null)
              .map((_, i) => <Skeleton key={i} height={48} />)
          : filteredReportsInstances.map(reportsInstance => {
              const isMarked = markedIds.has(reportsInstance.id)
              return (
                <ReportItem
                  key={reportsInstance.id}
                  data-testid={`ReportItem:${reportsInstance.id}`}
                  onClick={() =>
                    push({
                      panel: Panels.report,
                      title: reportsInstance.title,
                      props: {
                        reportInstanceId: reportsInstance.id
                      }
                    })
                  }
                  title={reportsInstance.title}
                  isMarked={isMarked}
                  onToggleMark={() =>
                    toggleMark(
                      reportsInstance.id,
                      ContentMarkType.Report
                    )
                  }
                />
              )
            })}
      </Flex>
    </Flex>
  )
}

export default ReportsPanel
